// Card copy for a payment suggestion, built from the resolved facts the intelligence-service returns
// on `signal.payment` — what it targets, for how much, and when it sends. The service deliberately
// sends facts only (no wording, no formatted money, no locale), so all of the phrasing lives here.
// Plain module (no component imports) so it stays unit-testable.
import { invoicePeriodText } from "./invoicePeriod";

// The same wording the customer's own payment email uses for each invoice bucket, so the card and the
// email never describe the same request differently.
const BUCKET_TEXT = {
  None: "Total balance",
  Overdue: "Overdue invoices",
  OverdueAndDueThisMonth: "Invoices overdue & due this month",
  All: "All invoices",
  SpecificInvoices: "Specific invoices",
};

// A null amount means the service could not establish a figure. Number(null) and Number("") are both
// 0, so those have to be rejected before the finite check or "no figure" would print as "£0.00".
// Whole amounts read as "£4,000", not "£4,000.00" — pence only appear when there are pence.
function formatMoney(value) {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  const pence = Number.isInteger(number) ? 0 : 2;
  return `£${number.toLocaleString("en-GB", {
    minimumFractionDigits: pence,
    maximumFractionDigits: 2,
  })}`;
}

// The short form of a bucket, for the parenthetical beside a template name ("… (Overdue)"). Matches
// the customer timeline's vocabulary, where the same buckets are already abbreviated this way.
const BUCKET_SHORT_TEXT = {
  Overdue: "Overdue",
  OverdueAndDueThisMonth: "Overdue + Due",
  All: "All invoices",
};

// An instant (ISO with offset), e.g. a scheduled send time.
function parseInstant(iso) {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDayMonthTime(date) {
  const day = date.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
  const time = date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  return `${day}, ${time}`;
}

// Invoices the user named themselves, listed: "Invoice 49923" / "Invoices 12351, 11623 & 21425", and
// past four "Invoices 1, 2, 3 & 2 more" so the description cannot outgrow the card.
//
// Only reached for a NamedByUser selection. A bucket or a period is described by what was ASKED for,
// never by the invoices it resolved to — the service withholds the numbers in those cases, and
// paymentScopeText does not route them here either.
const MAX_LISTED_INVOICES = 4;

function namedInvoicesText(numbers) {
  const list = (numbers ?? []).filter(Boolean);
  if (list.length === 0) return "Selected invoices";
  if (list.length === 1) return `Invoice ${list[0]}`;

  const [shown, tail] =
    list.length <= MAX_LISTED_INVOICES
      ? [list.slice(0, -1), list[list.length - 1]]
      : [list.slice(0, MAX_LISTED_INVOICES - 1), `${list.length - (MAX_LISTED_INVOICES - 1)} more`];
  return `Invoices ${shown.join(", ")} & ${tail}`;
}

// How many invoices, when that is all we can honestly say about the set.
function invoiceCountText(selection) {
  const count = selection.count ?? 0;
  return `${count} ${count === 1 ? "invoice" : "invoices"}`;
}

// A period the user described reports as the period — never as the invoices it happened to resolve to,
// which the service withholds for exactly that reason. With no dates at all the count is the only fact
// left to state.
function periodText(selection, asAt) {
  const named = invoicePeriodText(
    selection.periodFrom, selection.periodTo, asAt, selection.periodBasis);
  return named ?? invoiceCountText(selection);
}

// What the request targets. Null for a flat sum or a schedule, which the amount already describes.
export function paymentScopeText(payment) {
  const selection = payment?.selection;
  if (!selection) return null;
  switch (selection.kind) {
    case "Statement":
      return "Full account statement";
    case "Bucket":
      return BUCKET_TEXT[selection.bucket] ?? "Amount due";
    case "Period":
      return periodText(selection, payment.amount?.asAt);
    case "NamedByUser":
      return namedInvoicesText(selection.numbers);
    // The service could not attribute the choice to the user, so the set is stated by size. Listing it
    // would read as "the invoices you asked for" when nobody knows that to be true.
    case "Unattributed":
      return invoiceCountText(selection);
    default:
      return null;
  }
}

// The figure, or an honest stand-in. A null value means the service could not establish one — it is
// never a zero, so it must not print as "£0.00".
export function paymentAmountText(payment) {
  return formatMoney(payment?.amount?.value) ?? "Amount settled at payment";
}

// When it goes out, and why then: an immediate request has no clock time, so none is invented; a
// scheduled one names the configured default as a default rather than implying the user chose it.
export function paymentSendText(payment) {
  const schedule = payment?.schedule;
  if (!schedule) return null;

  const verb = schedule.instalments?.length > 1 ? "First sends" : "Sends";
  if (schedule.source === "OnApproval") return "Sends when you accept";

  const sendsAt = parseInstant(schedule.sendsAt);
  if (!sendsAt) return `${verb} on the scheduled date`;

  const when = formatDayMonthTime(sendsAt);
  return schedule.source === "DefaultPaymentRequestSendTime"
    ? `${verb} ${when} (default send time)`
    : `${verb} ${when}`;
}

// What the request covers, shown in brackets after the amount when a template name has taken the
// leading slot — otherwise that fact would be lost. Buckets use their short form; a period or a named
// set reads the same as it would on its own.
function qualifierText(payment) {
  const selection = payment?.selection;
  switch (selection?.kind) {
    case "Bucket":
      return BUCKET_SHORT_TEXT[selection.bucket] ?? null;
    case "Statement":
      return "Statement";
    case "Period":
    case "NamedByUser":
    case "Unattributed":
      return paymentScopeText(payment);
    default:
      return null;
  }
}

// The leading phrase: the template the customer will receive, else what the request targets, else a
// plain flat sum.
function subjectText(payment) {
  if (payment.templateName) return payment.templateName;
  return paymentScopeText(payment) ?? "Custom amount";
}

// The card's description: "<subject> · <amount>[ (<qualifier>)]", plus when it sends for scheduled
// work. An immediate request says nothing about timing — the Request button already means "now".
// Null when the service resolved no payment facts, in which case the caller falls back to the
// model's own sentence.
export function paymentSummary(payment) {
  if (!payment?.amount || !payment?.schedule) return null;

  const instalmentCount = payment.schedule.instalments?.length ?? 0;
  if (instalmentCount > 1) {
    return [
      `${instalmentCount} payments totalling ${paymentAmountText(payment)}`,
      paymentSendText(payment),
    ]
      .filter(Boolean)
      .join(" · ");
  }

  const qualifier = payment.templateName ? qualifierText(payment) : null;
  const amount = `${paymentAmountText(payment)}${qualifier ? ` (${qualifier})` : ""}`;
  const scheduled = payment.schedule.source !== "OnApproval" ? paymentSendText(payment) : null;

  return [subjectText(payment), amount, scheduled].filter(Boolean).join(" · ");
}
